// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import {
  useCreateCollectBySiteIdMutation,
  useFetchCollectAreaQuery,
} from 'apis/collect_user/collectUserApi';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { CollectEmissions } from 'components/organisms/CollectEmissions';
import CollectEmissionsSelectTruckModal from 'components/organisms/CollectEmissionsSelectTruckModal';
import { WeightNoticeModal } from 'components/organisms/WeightNoticeModal';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { addition } from 'util/Calc';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Path } from 'routes';
import { TSelectTruckModal } from 'types/Collect';
import styles from './CollectRegist.module.scss';
import { CollectPhotoModal } from 'components/molecules/CollectPhotoModal';
import Popover from 'components/atoms/Popover';
import { getApiPath, uniqRandomNumber } from 'util/commons';
import { Form } from 'react-final-form';
import { handleError, isError } from 'util/errorHandler';
import { DATE_FORMAT, JWNET_MAXIMUM_WEIGHT } from 'util/ConstantValues';
import _debounce from 'lodash/debounce';
import { normalizeNumber } from 'util/commons';
import {
  OptionItem,
  SelectFieldMultipleChoise,
} from 'components/molecules/SelectFieldMultipleChoise';
import { TCollectEmission } from 'util/Types';
import { createAxios } from 'ts/createAxios';
import { API_ENDPOINTS } from 'util/endPoints';
import dayjs from 'dayjs';
import CollectDateSelectModal from 'components/molecules/CollectDateSelectModal';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { IconFilter } from 'components/atoms/icons/IconFilter';
import _omit from 'lodash/omit';

type FilterOptions = {
  wasteUnitId?: string | number;
  tenantIds?: (number | string)[];
  dateRange?: [string, string];
  trigger?: number;
};

function Collects() {
  const { t } = useTranslation();
  const params = useParams();
  const { push } = useHistory();

  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [filter, setFilter] = useState<FilterOptions>({});
  const api = createAxios();
  const [emissions, setEmissions] = useState<TCollectEmission[]>([]);
  const [wasteUnitOptions, setWasteUnitOptions] = useState<OptionItem[]>([]);
  const [tenantOptions, setTenantOptions] = useState<OptionItem[]>([]);
  const [truckModal, setTruckModal] = useState<boolean>(false);
  const [siteSelected, setSiteSelected] = useState<SelectOption>({
    label: '',
    value: '',
  });
  const [error, setError] = useState<string>('');
  const [isWeightNoticeModalOpen, setIsWeightNoticeModalOpen] =
    useState<boolean>(false);
  const [isCollectPhotoModalOpen, setIsCollectPhotoModalOpen] =
    useState<boolean>(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [bottomHeight, setBottomHeight] = useState<number>();
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [filterOpened, setFilterOpened] = useState(false);
  const [tmpSubmittingData, setTmpSubmittingData] = useState<{
    imageData?: string;
    truckValues?: TSelectTruckModal;
    receivedDatetime?: dayjs.Dayjs;
  }>();
  const { data: areas } = useFetchCollectAreaQuery();

  const [fnCreate] = useCreateCollectBySiteIdMutation();

  const fetchWasteUnits = (p?: { resetOtherFilters?: boolean }) => {
    const { resetOtherFilters = false } = p || {};

    api
      .get<OptionItem[]>(
        getApiPath(API_ENDPOINTS.COLLECT_WASTE_UNITS, { id: params['id'] })
      )
      .then((res) => {
        setWasteUnitOptions(res.data ?? []);

        if (resetOtherFilters) {
          setFilter((prev) => ({
            wasteUnitId:
              prev.wasteUnitId != null &&
              res?.data?.some(
                (wu) => wu.id.toString() === prev.wasteUnitId?.toString()
              )
                ? prev.wasteUnitId
                : res?.data?.[0]?.id,
            trigger: uniqRandomNumber(),
          }));
        } else {
          setFilter((prev) => ({ ...prev, wasteUnitId: res?.data?.[0]?.id }));
        }
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const fetchTenants = () => {
    api
      .get<OptionItem[]>(
        getApiPath(API_ENDPOINTS.COLLECT_TENANTS, { id: params['id'] })
      )
      .then((res) => {
        if (res.data.length > 0) {
          const unidentifyTenant: OptionItem = {
            id: '',
            name: t('un_collect.unidentify_tenant'),
          };

          setTenantOptions(res.data.concat(unidentifyTenant));
        } else {
          setTenantOptions([]);
        }
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const fetchCollectsById = (filter: FilterOptions) => {
    api
      .get<TCollectEmission[]>(
        getApiPath(API_ENDPOINTS.COLLECTS_BY_ID, { id: params['id'] }),
        {
          params: {
            wasteUnitId: filter.wasteUnitId,
            tenantIds: filter.tenantIds,
            dateFrom: filter.dateRange?.[0] || undefined,
            dateTo: filter.dateRange?.[1] || undefined,
          },
        }
      )
      .then((res) => {
        setEmissions(res.data);
        setCheckedIds((prev) =>
          prev.filter((id) => res.data.some((e) => e.id === id))
        );
      })
      .catch((e) => {
        handleError(e);
      });
  };

  useEffect(() => {
    fetchWasteUnits();
    fetchTenants();
  }, []);

  useEffect(() => {
    if (filter.wasteUnitId != null) {
      fetchCollectsById(filter);
    } else {
      setEmissions([]);
    }
  }, [filter]);

  const closeWeightNoticeModal = () => {
    setIsWeightNoticeModalOpen(false);
  };

  const closeCollectPhotoModal = () => {
    setTmpSubmittingData((prev) => ({
      ...prev,
      imageData: undefined,
    }));
    setIsCollectPhotoModalOpen(false);
  };

  const areaName = useMemo(() => {
    const record = areas?.filter((area) => area.areaId === params['id'])[0];
    if (record) return record.areaName;
  }, [areas]);

  const totalWeight = useMemo(
    () =>
      emissions
        ?.filter((emission) => checkedIds.includes(emission.id))
        .reduce(
          (prev, emission) => addition(prev, parseFloat(emission.weight)),
          0
        ),
    [checkedIds]
  );

  const isJWNETTotalWeightExceeded = useMemo(
    () =>
      emissions?.[0]?.wasteUnit.isEdiEnabled &&
      (totalWeight ?? 0) > JWNET_MAXIMUM_WEIGHT,
    [emissions, totalWeight]
  );

  const totalActivatedFilters = useMemo(
    () =>
      Object.values(_omit(filter, ['trigger'])).filter(
        (val) =>
          val != null && val !== '' && (!Array.isArray(val) || val.length)
      ).length,
    [filter]
  );

  useLayoutEffect(() => {
    const init = () => {
      if (bottomRef.current) {
        setBottomHeight(bottomRef.current.clientHeight);
      }
      setWindowWidth(window.innerWidth);
    };
    init();
    const initOnResize = _debounce(init, 200);
    window.addEventListener('resize', initOnResize);

    return () => {
      window.removeEventListener('resize', initOnResize);
    };
  }, [isJWNETTotalWeightExceeded, t]);

  const closeTruckModal = () => {
    setTruckModal(false);
    setTmpSubmittingData((prev) => ({
      ...prev,
      truckValues: undefined,
    }));
  };

  const onSubmit = async (p?: { mode?: 'finish' | 'continue' }) => {
    const { mode = 'finish' } = p ?? {};

    if (!checkedIds.length) {
      toast.error(t('collect_registration.submit_validate_error'));

      return;
    }

    if (!tmpSubmittingData?.receivedDatetime) {
      setTmpSubmittingData((prev) => ({
        ...prev,
        receivedDatetime: dayjs(),
      }));

      return;
    }

    if (error) {
      toast.error(error);

      return;
    }

    const data: {
      emissionIds: string[];
      imageData?: string;
      truckId?: number;
      receivedDatetime: string;
    } = {
      emissionIds: checkedIds,
      receivedDatetime: tmpSubmittingData.receivedDatetime.format(
        DATE_FORMAT.slaYMDHm
      ),
    };

    if (tmpSubmittingData?.imageData) {
      data.imageData = tmpSubmittingData.imageData;
    }

    if (tmpSubmittingData?.truckValues) {
      data.truckId = Number(tmpSubmittingData.truckValues.truckId);
    }

    fnCreate({
      id: params['id'],
      data,
    })
      .then(function (res) {
        if (isError(res)) return;

        toast.success(t('messages.M_087'));
        setCheckedIds([]);
        fetchCollectsById(filter);
        setTruckModal(false);
        setIsCollectPhotoModalOpen(false);
        setTmpSubmittingData(undefined);

        if (mode === 'finish') {
          push(`${Path.collects}${window.location.search}`);
        } else if (checkedIds.length >= emissions.length) {
          fetchWasteUnits({
            resetOtherFilters: true,
          });
          fetchTenants();
        }
      })
      .catch(function (e) {
        toast.error(
          e?.response?.data?.message ||
            t('collect_registration.submit_api_error')
        );
      });
  };

  const onTruckSubmit = async (values: TSelectTruckModal) => {
    setTmpSubmittingData((prev) => ({
      ...prev,
      truckValues: values,
      receivedDatetime: dayjs(),
    }));
  };

  const onPreSubmit = async () => {
    const firstRecord = emissions?.[0];
    const isEdiEnabled = Boolean(firstRecord?.wasteUnit?.isEdiEnabled);

    if (isEdiEnabled) {
      setSiteSelected({
        label: firstRecord?.collectionSiteName ?? '',
        value: firstRecord?.collectionSiteId ?? '',
      });
      setTruckModal(true);
    } else {
      await onSubmit();
    }
  };

  return (
    <PrimaryTemplate
      h1={t('collect_registration.title')}
      prevFunction={() => push(`${Path.collects}${window.location.search}`)}
      className={styles.containerCollectRegistration}
    >
      <div>
        <div className={styles.areaName}>
          <div className={styles.areaNameTitle}>
            <Popover
              positions={['top', 'bottom', 'right', 'left']}
              containerStyle={{
                zIndex: '100000',
              }}
              autoGen={{
                wrapperStyle: {
                  fontWeight: 'bold',
                  paddingLeft: 16,
                  paddingRight: 8,
                },
              }}
            >
              <>{areaName}</>
            </Popover>
          </div>

          <div
            className={styles.filterIcon}
            onClick={() => setFilterOpened((prev) => !prev)}
          >
            <IconFilter
              color={totalActivatedFilters > 0 ? '#86ea5c' : undefined}
            />
            {totalActivatedFilters > 0 && totalActivatedFilters}
          </div>

          <div
            className={styles.filter}
            style={{
              display: windowWidth > 768 || filterOpened ? 'block' : 'none',
            }}
          >
            <Form<FilterOptions> onSubmit={() => {}}>
              {() => (
                <div className={styles.filterBox}>
                  <div className={styles.filterItem}>
                    <SelectField
                      value={filter.wasteUnitId?.toString()}
                      name="wasteUnitId"
                      placeholder={t('collect_registration.content') || ''}
                      options={
                        wasteUnitOptions.map((item) => ({
                          label: item.name,
                          value: item.id,
                        })) || []
                      }
                      isDarkmode
                      usePortal
                      showPlaceholderOption
                      onChange={(value) => {
                        setFilter((prev) => ({
                          ...prev,
                          wasteUnitId: value,
                        }));
                      }}
                      fontSize="large"
                    />
                  </div>

                  <div className={styles.filterItem}>
                    <SelectFieldMultipleChoise
                      options={tenantOptions}
                      onChange={(tenants) => {
                        setFilter((prev) => ({
                          ...prev,
                          tenantIds: tenants.map((tenant) => tenant.id),
                        }));
                      }}
                      disabled={!tenantOptions.length}
                      placeholder={t('dashboard.tenant_name') || 'テナント名'}
                      name="tenants"
                      selectedOptions={filter.tenantIds || []}
                      darkMode
                      usePortal
                      allowClear
                      fontSize="large"
                    />
                  </div>

                  <div className={styles.filterItem}>
                    <TableFilterCalendar
                      value={filter.dateRange}
                      expanded={{
                        placeholder: t('common.placeholder.select_date_range'),
                        icon: <IconCalendarTablet />,
                        className: styles.datetimeInput,
                      }}
                      portalPositionPoint={(ref) => ref}
                      onChange={(values) =>
                        setFilter((prev) => ({
                          ...prev,
                          dateRange: values,
                        }))
                      }
                      align="right"
                      endDateRequired
                      fontSizeType="large"
                    />
                  </div>
                </div>
              )}
            </Form>
          </div>
        </div>
        <CollectEmissions
          emissions={emissions || []}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          setError={setError}
          tableStyle={{
            height: `calc(100vh - ${
              windowWidth <= 768 ? 100 : 116
            }px - ${bottomHeight}px)`,
          }}
        />
      </div>

      <div ref={bottomRef}>
        <div className={styles.totalWrapper}>
          <div className={styles.total}>
            <div className={styles.totalTitle}>
              {t('collect_registration.label_total')}
            </div>
            <div
              className={styles.totalContent}
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <span>
                {t('collect_registration.total_weight', {
                  weight: normalizeNumber({
                    value: totalWeight,
                  }),
                })}
              </span>

              <span>
                {t('collect_registration.total_count', {
                  count: normalizeNumber({
                    value: checkedIds
                      .map((id) => emissions.find((e) => e.id === id))
                      .filter((e) => e && Number(e.weight || 0) > 0).length,
                  }) as never,
                })}
              </span>
            </div>
          </div>

          {isJWNETTotalWeightExceeded && (
            <div className={styles.error}>
              {t('collect_registration.jwnet_weight_exceeded_error', {
                value: JWNET_MAXIMUM_WEIGHT,
              })}
            </div>
          )}
        </div>
        <div className={styles.bottomBtn}>
          <button
            className={styles.btnEditing}
            type="button"
            disabled={checkedIds.length === 0}
            onClick={() => {
              setIsWeightNoticeModalOpen(true);
            }}
          >
            {t('collect_registration.button_notify_anomalies')}
          </button>

          <button
            className={styles.btnRequest}
            disabled={checkedIds.length === 0 || isJWNETTotalWeightExceeded}
            onClick={onPreSubmit}
          >
            {t('collect_registration.button_to_recover')}
          </button>

          <button
            className={styles.btnRequest}
            disabled={checkedIds.length === 0}
            onClick={() => {
              setIsCollectPhotoModalOpen(true);
            }}
          >
            {t('collect_registration.button_to_collect_photo')}
          </button>
        </div>
      </div>

      {isWeightNoticeModalOpen && (
        <WeightNoticeModal
          isOpen={isWeightNoticeModalOpen}
          closeEditModal={closeWeightNoticeModal}
          targetIds={checkedIds}
          areaId={params['id']}
          areaName={areaName}
        />
      )}

      {truckModal && siteSelected && (
        <CollectEmissionsSelectTruckModal
          closeModal={closeTruckModal}
          onSubmit={onTruckSubmit}
          isOpen={truckModal}
          site={siteSelected}
        />
      )}

      {isCollectPhotoModalOpen && (
        <CollectPhotoModal
          isOpen={isCollectPhotoModalOpen}
          closeEditModal={closeCollectPhotoModal}
          onSubmit={(val) =>
            setTmpSubmittingData((prev) => ({
              ...prev,
              imageData: val,
              receivedDatetime: dayjs(),
            }))
          }
          onClose={closeCollectPhotoModal}
          targetEmissions={
            emissions?.filter((emission) => checkedIds.includes(emission.id)) ??
            []
          }
          areaId={params['id']}
          areaName={areaName}
        />
      )}

      {tmpSubmittingData?.receivedDatetime && (
        <CollectDateSelectModal
          state={[
            tmpSubmittingData.receivedDatetime,
            (date) =>
              setTmpSubmittingData((prev) => ({
                ...prev,
                receivedDatetime: date,
              })),
          ]}
          onSubmitAndFinish={onSubmit}
          onSubmitAndContinue={() => onSubmit({ mode: 'continue' })}
        />
      )}
    </PrimaryTemplate>
  );
}

export default Collects;
